import { Info } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  DiscordBotHealth,
  DiscordBotStatus,
  DiscordGuildSummary,
} from '@/Config/api/queryClient';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { safeFormat } from '@/Domain/utils/safeDate';

type HealthTone = 'success' | 'warning' | 'destructive' | 'muted';

const TONE_DOT_CLASS: Record<HealthTone, string> = {
  success: 'bg-success',
  warning: 'bg-warning',
  destructive: 'bg-destructive',
  muted: 'bg-foreground-subtle',
};

const HEALTH_TONE: Record<DiscordBotHealth, HealthTone> = {
  error: 'destructive',
  token_invalid: 'destructive',
  unknown: 'muted',
  intent_off: 'warning',
  disabled: 'muted',
  // The token works but no gateway handshake ever confirmed the bot started.
  unconfirmed: 'warning',
  live: 'success',
};

interface HealthLineProps {
  tone: HealthTone;
  label: string;
  tooltip?: ReactNode;
  tooltipLabel?: string;
  loading?: boolean;
  'data-testid'?: string;
}

/** One dot + label, optionally explained by a tooltip. Shared so both Discord tiers read identically. */
export const HealthLine = ({
  tone,
  label,
  tooltip,
  tooltipLabel,
  loading,
  'data-testid': testId,
}: HealthLineProps) => {
  if (loading) {
    return (
      <span className="flex items-center gap-1.5" data-testid={testId}>
        <Spinner className="size-3.5 text-foreground-subtle" />
        <span className="text-sm text-muted-foreground">{label}</span>
      </span>
    );
  }
  return (
    // A span, not a div: the line also renders inside IntegrationConnectionCard's <p> values.
    <span className="flex items-center gap-1.5" data-testid={testId} data-tone={tone}>
      <span className={`inline-block size-2.5 shrink-0 rounded-full ${TONE_DOT_CLASS[tone]}`} />
      <span className="text-sm text-muted-foreground">{label}</span>
      {tooltip && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={tooltipLabel}
                className="inline-flex cursor-help rounded-sm text-foreground-subtle focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/25"
              >
                <Info className="size-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent>{tooltip}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </span>
  );
};

export interface DiscordBotHealthLineProps {
  status: DiscordBotStatus | undefined;
  /** True only while the first check is still outstanding, so a failed one stops spinning. */
  loading: boolean;
  error?: boolean;
}

/** Own-bot health for the Advanced sheet, led by the live getMe rather than the stored flags. */
export const DiscordBotHealthLine = ({ status, loading, error }: DiscordBotHealthLineProps) => {
  const { t } = useTranslation(['integration']);
  // React Query keeps the previous verdict when a refetch fails, and a green dot sourced from
  // a check that just failed is the same over-claim as one we never ran.
  if (error || !status) {
    if (loading && !error) {
      return (
        <HealthLine
          tone="muted"
          label={t('integration:discord.health.checking')}
          loading
          data-testid="discord-advanced-health"
        />
      );
    }
    return (
      <HealthLine
        tone="muted"
        label={t('integration:discord.health.unknown')}
        data-testid="discord-advanced-health"
      />
    );
  }
  // Mid rolling-deploy an older backend answers with name/username and no health at all.
  const health: DiscordBotHealth = HEALTH_TONE[status.health] ? status.health : 'unknown';
  const confirmedAt = safeFormat(status.gateway_confirmed_at, 'd MMM yyyy, HH:mm');
  return (
    <HealthLine
      tone={HEALTH_TONE[health]}
      label={t(`integration:discord.health.${health}`)}
      tooltip={
        confirmedAt
          ? t('integration:discord.health.confirmed_tooltip', { timestamp: confirmedAt })
          : t('integration:discord.health.never_confirmed_tooltip')
      }
      tooltipLabel={t('integration:discord.health.details_label')}
      data-testid="discord-advanced-health"
    />
  );
};

export interface DiscordGuildHealthLineProps {
  guildId: string;
  enabled: boolean;
  state: DiscordGuildSummary['state'];
  /** undefined while loading, null when Discord could not be reached. */
  present: boolean | null | undefined;
  loading: boolean;
}

/** Per-server health for the one-click sheet: is the shared bot actually still in there. */
export const DiscordGuildHealthLine = ({
  guildId,
  enabled,
  state,
  present,
  loading,
}: DiscordGuildHealthLineProps) => {
  const { t } = useTranslation(['integration']);
  const testId = `discord-server-health-${guildId}`;
  // We only ask Discord about ACTIVE guilds, so a server still being added has nothing to
  // report — saying "could not reach Discord" would attribute silence to a call never made.
  if (state !== 'ACTIVE') return null;
  if (loading) {
    return (
      <HealthLine
        tone="muted"
        label={t('integration:discord.health.checking')}
        loading
        data-testid={testId}
      />
    );
  }
  if (present === false) {
    return (
      <HealthLine
        tone="destructive"
        label={t('integration:discord.health.guild_missing')}
        tooltip={t('integration:discord.health.guild_missing_tooltip')}
        tooltipLabel={t('integration:discord.health.details_label')}
        data-testid={testId}
      />
    );
  }
  if (!enabled) {
    return (
      <HealthLine
        tone="muted"
        label={t('integration:discord.health.guild_paused')}
        data-testid={testId}
      />
    );
  }
  if (present === null || present === undefined) {
    return (
      <HealthLine
        tone="muted"
        label={t('integration:discord.health.unknown')}
        data-testid={testId}
      />
    );
  }
  return (
    <HealthLine
      tone="success"
      label={t('integration:discord.health.guild_live')}
      data-testid={testId}
    />
  );
};
